import { Button, Drawer } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { TbDownload, TbEye, TbFolder, TbFolderOpen, TbPencil } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { diskBrowserStore } from '../Stores/DiskBrowserStore'
import { diskPropertiesStore as store } from '../Stores/DiskPropertiesStore'
import { fileViewerStore } from '../Stores/FileViewerStore'
import { DiskAccessSection } from './DiskAccessSection'
import { DiskNodeInfoPanel } from './DiskNodeInfoPanel'
import { useDiskActions } from './useDiskActions'

import styles from './Disk.module.css'

/** Ширина дровера на широкому екрані; на телефоні — уся ширина. */
export const DISK_PROPS_WIDTH = 400

/** Позначка кнопок «Властивості»: клік по них перемикає дровер, а не закриває. */
export const DISK_PROPS_TRIGGER = 'data-disk-props-trigger'

/**
 * Що не вважається «кліком поза дровером»: спливні вікна antd, відкриті з
 * самого дровера чи над ним (перейменування, «Поділитися», меню, підказки,
 * повідомлення), і кнопки «Властивості».
 */
const KEEP_OPEN_SELECTOR = [
    '.ant-modal-root',
    '.ant-modal-wrap',
    '.ant-dropdown',
    '.ant-popover',
    '.ant-tooltip',
    '.ant-select-dropdown',
    '.ant-picker-dropdown',
    '.ant-message',
    '.ant-notification',
    `[${DISK_PROPS_TRIGGER}]`,
].join(',')

/**
 * Дровер «Властивості» (54631) — справа поверх екрана диска, без затемнення:
 * список під ним лишається видимим; клік поза дровером його закриває, а
 * кнопка «Властивості» іншого рядка перемикає. Як `FilePreviewDrawer`
 * референсу: шапка з мініатюрою чи іконкою виду, дії, реквізити, журнал.
 *
 * Відкривають: «Властивості» в меню рядка й на панелі вибору (кнопки в шапці
 * немає: при вибраному рядку шапку закриває панель вибору), клавіша `I`. Файл не відкривається — переглядач лише подвійним
 * кліком чи «Відкрити». Закривають Esc, хрестик і клік поза дровером.
 */
export const DiskPropertiesDrawer = observer(({ compact }: { compact: boolean }) => {
    const actions = useDiskActions()
    const isOpen = store.isOpen
    const drawerClass = styles['propsDrawer']

    // Клік поза дровером закриває його (маски немає, тож antd цього не робить сам)
    useEffect(() => {
        if (!isOpen) return undefined

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target

            if (!(target instanceof Element) || fileViewerStore.isOpen) return
            if (drawerClass !== undefined && target.closest(`.${drawerClass}`) !== null) return
            if (target.closest(KEEP_OPEN_SELECTOR) !== null) return

            store.close()
        }

        document.addEventListener('pointerdown', onPointerDown, true)

        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [isOpen, drawerClass])

    const node = store.info.node
    const can = node?.permissions ?? {}
    const isFile = node?.type === 'file'
    const showParent =
        diskBrowserStore.isSearchActive || diskBrowserStore.location?.kind === 'recent' || diskBrowserStore.isSharedList

    const lead =
        node !== null && store.thumbUrl !== null ? (
            <span className={classNames(styles['propsThumb'])}>
                <img
                    src={store.thumbUrl}
                    alt={node.name}
                    onError={() => store.thumbFailed()}
                    data-testid="disk-properties-thumb"
                />
            </span>
        ) : undefined

    const buttons =
        node !== null ? (
            <>
                <Button
                    type="primary"
                    icon={isFile ? <TbEye /> : <TbFolderOpen />}
                    onClick={() => actions.open(node)}
                    data-testid="disk-properties-open"
                >
                    Відкрити
                </Button>
                {isFile && can.read === true && (
                    <Button icon={<TbDownload />} onClick={() => void actions.download(node)}>
                        Скачати
                    </Button>
                )}
                {can.update === true && (
                    <Button icon={<TbPencil />} onClick={() => diskBrowserStore.openRename(node)}>
                        Перейменувати
                    </Button>
                )}
                {showParent && (
                    <Button icon={<TbFolder />} onClick={() => actions.openParent(node)}>
                        Показати в папці
                    </Button>
                )}
            </>
        ) : null

    return (
        <Drawer
            open={store.isOpen}
            onClose={() => store.close()}
            placement="right"
            title="Властивості"
            size={compact ? '100%' : DISK_PROPS_WIDTH}
            mask={false}
            autoFocus={false}
            focusable={{ trap: false, focusTriggerAfterClose: false }}
            // Переглядач (z-index 1000) відкривається над дровером
            zIndex={990}
            className={classNames(styles['propsDrawer'])}
        >
            <div data-testid="disk-properties">
                <DiskNodeInfoPanel
                    info={store.info}
                    lead={lead}
                    actions={buttons}
                    {...(node?.type === 'folder' ? { folderItems: store.folderItems } : {})}
                    {...(node !== null
                        ? {
                              access: (
                                  <DiskAccessSection
                                      access={store.access}
                                      error={store.accessError}
                                      onManage={() => actions.share(node)}
                                  />
                              ),
                          }
                        : {})}
                />
            </div>
        </Drawer>
    )
})
